import styles from "@/styles/hottest-deals/index.module.scss"
import { useState, useEffect } from 'react'

import DefaultLayout from "@/components/DefaultLayout"
import BreadCrum from '@/components/global/BreadCrum'
import Csotd from '@/components/hottestDeals/Csotd'
import CrazyTopSellers from '@/components/hottestDeals/CrazyTopSellers'
import FeaturedDeals from '@/components/hottestDeals/FeaturedDeals'
import HotSchema from '@/components/hottestDeals/HotSchema'
import MiddleSubscribe from "@/components/MiddleSubscribe"

import hottestDealsApi from '@/service/modules/hottestDealsApi'
import systemApi from '@/service/modules/systemApi'

import Head from "next/head"
import { getCookie, setCookie } from '@/utils/cookie'
import { useRouter } from 'next/router'

import getConfig from 'next/config'
const { publicRuntimeConfig } = getConfig()
const apiDomain = publicRuntimeConfig.apiDomain

export default function HottestDealsPage() {
  interface BreadcrumbItem {
    name: string;
    url: string;
    current: boolean;
  }

  interface ProductItem {
    productID: string;
    name: string;
    url: string;
    imgUrl: string;
    reviewCount: number;
    price: number;
    normalPrice: number;
    freeShipping: number;
    onSale: number;
    reduced: number;
    isNew: number;
    position?: number,
    location?: string;
    isEmailOnly: any,
    promotionTypeID: string | number;
    promotion: {
      productID: number;
      promotionType: number;
      promotionPrice: number;
      normalPrice: number;
      endDate: string;
      status: number;
      notified: number;
      pointRate: string;
      promotionRate: number;
    };
    review: {
      avgRank: number;
      total: number;
    };
  }

  interface CsotdData {
    productID: string;
    name: string;
    url: string;
    imgUrl: string;
    filename: string;
    review: number;
    reviewCount: number;
    price: number;
    normalPrice: number;
    freeShipping: number;
    onSale: number;
    reduced: number;
    isNew: number;
    position?: number,
    location?: string;
    isEmailOnly: any,
    promotionTypeID: string | number;
    promotion: {
      productID: number;
      promotionType: number;
      promotionPrice: number;
      normalPrice: number;
      endDate: string;
      status: number;
      notified: number;
      pointRate: string;
      promotionRate: number;
    };
    crazyDeal: {
      hoursLeft: number;
      minutesLeft: number;
      secondsLeft: number;
    } | undefined
  }

  interface HotSchemaItem {
    itemID: number;
    name: string;
    title: string;
    extraTitle: string;
    isEnable: number;
    url: string;
    logoUrl: string;
    items: ProductItem[]
  }

  interface BannerItem {
    imgUrl: string;
    title: string;
    link: string;
  }

  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [csotdData, setCsotdData] = useState<CsotdData | null>()
  const [topSellersData, setTopSellersData] = useState<ProductItem[]>([]);
  const [featuredDealsData, setFeaturedDealsData] = useState<ProductItem[][]>([]);
  const [hotSchemaData, setHotSchemaData] = useState<HotSchemaItem[]>([]);
  const [midBanner, setMidBanner] = useState<BannerItem | null | undefined>()
  const [midBannerPad, setMidBannerPad] = useState<BannerItem | null | undefined>()

  // 获取面包屑数据
  async function getBreadcrumbData() {
    try {
      let productData = await systemApi.getBreadcrumb('hottest-deals')

      if (productData.data) {
        setBreadcrumb(productData.data.breadCrumb)
      }
    } catch (error) {
      console.log(`Best Deals数据获取失败： `, error)
    }
  }

  // 获取csotd模块数据
  async function getCrazyDealData() {
    try {
      let csotdData = await systemApi.getCrazyDealOtd()

      if (csotdData.data) {
        setCsotdData(csotdData.data.product)
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  // 获取topSellers模块数据
  async function getTopSellerData() {
    try {
      let resData = await hottestDealsApi.getTopSeller()

      if (resData.data && resData.data.length) {
        // 整理图片
        resData.data.forEach((item: any) => {
          item.imgUrl = item.filename || item.newThumbnail
        })

        setTopSellersData(resData.data)
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  // 将数据分类，商品数组拆分为多个页面（每个page 8条数据）
  function paginateProducts(products: ProductItem[], itemsPerPage: number): ProductItem[][] {
    const paginatedArray: ProductItem[][] = [];
    const totalPages = Math.ceil(products.length / itemsPerPage);

    for (let i = 0; i < totalPages; i++) {
      const startIndex = i * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const page = products.slice(startIndex, endIndex);
      paginatedArray.push(page);
    }

    return paginatedArray;
  }

  // 获取Featured Deals模块数据
  async function getFeaturedDealsData() {
    try {
      let resData = await hottestDealsApi.getFeaturedDeals()

      if (resData.data && resData.data.length) {
        // 整理图片
        resData.data.forEach((item: any) => {
          item.imgUrl = item.filename || item.newThumbnail
        })

        // 将每个页面包含 8 个商品的数组
        const paginatedProducts = paginateProducts(resData.data, 8);

        setFeaturedDealsData(paginatedProducts)
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  // 获取Hot Schema模块数据
  async function getHotSchemaData() {
    try {
      let resData = await hottestDealsApi.getHotScheme()

      if (resData.data && resData.data.length) {
        // 整理图片
        resData.data.forEach((item: any) => {
          if (item.items && item.items.length) {
            item.items.forEach((productItem: any) => {
              productItem.imgUrl = productItem.filename || productItem.newThumbnail
            });
          }
        })

        setHotSchemaData(resData.data)
      }
    } catch (error) {
      console.log(`csotd数据获取失败： `, error)
    }
  }

  // 获取subscribe banner数据
  const getBannersData = async () => {
    try {
      const res = await systemApi.getFullBanners()
      const { midBanner, midBannerPad } = res.data.home

      setMidBanner(midBanner)
      setMidBannerPad(midBannerPad)
    } catch (error) {
      return { notFound: true }
    }
  }


  // 获取页面数据
  const getPageData = () => {
    // 获取面包屑数据
    getBreadcrumbData()

    // 获取csotd模块数据
    getCrazyDealData()

    // 获取topSellers模块数据
    getTopSellerData()

    // 获取Featured Deals模块数据
    getFeaturedDealsData()

    // 获取Hot Schema模块数据
    getHotSchemaData()

    // 获取subscribe banner数据
    getBannersData()
  }

  useEffect(() => {
    getPageData()
  }, [])

  const router = useRouter()
  // 判断是否有aid 或 recoveraid在url，有就将其
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    let lastAidValue = ''
    let lastRecoveraidValue = ''

    // 检查是否有"aid"参数，如有多个取最后
    if (Array.isArray(aid) && aid.length > 0) {
      lastAidValue = aid[aid.length - 1]
    } else if (typeof aid === 'string') {
      // 如果只有一个"aid"参数，它将是一个字符串
      lastAidValue = aid
    }

    // 检查是否有"recoveraid"参数，如有多个取最后
    if (Array.isArray(recoveraid) && recoveraid.length > 0) {
      lastRecoveraidValue = recoveraid[recoveraid.length - 1]
    } else if (typeof recoveraid === 'string') {
      // 如果只有一个"recoveraid"参数，它将是一个字符串
      lastRecoveraidValue = recoveraid
    }

    // 优先于异步请求，将aid存入cookie, 60秒过期（后续会发起请求重新设置）
    if (lastAidValue || lastRecoveraidValue) {
      let currentDomain = window.location.hostname;
      if (currentDomain == 'www.bestdeals.co.nz') {
        currentDomain = ".bestdeals.co.nz"
      }
      setCookie('affiliateid', lastAidValue || lastRecoveraidValue, { expires: 30, secure: true, domain: `${currentDomain}`, })
    }
  }, [router.query])

  // 发现本地有存储aid，重新请求数据
  const getProductDetailWithPromotion = async () => {
    if (!getCookie('affiliateid')) return

    getPageData()
  }

  // cookie有留存aid参数，则更新Email价格
  useEffect(() => {
    setTimeout(() => {
      let affiliateid = getCookie('affiliateid')
      if (affiliateid) getProductDetailWithPromotion()
    }, 400)
  }, [])

  return (
    <>
      <Head>
        <title>Hot Deals in New Zealand | Best Online Discounts & Offers | BestDeals.co.nz</title>
        <meta name="keywords" content="bestdeals, bestdeals co nz, best of deal, best of deals, hot deals"></meta>
        <meta name="description" content="Discover unbeatable deals and exclusive discounts on top-rated products at BestDeals. Shop our Hot Deals page for incredible savings in New Zealand. Limited-time offers!"></meta>
      </Head>

      <DefaultLayout >
        <div className={`${styles['page-hottest-deals']}`}>

          <div className={styles['page-container']}>
            {/* 面包屑 */}
            {
              !!(breadcrumb && breadcrumb.length) &&
              <BreadCrum BreadList={breadcrumb}></BreadCrum>
            }

            {
              !!(csotdData && topSellersData && topSellersData.length) &&
              <div className={styles['top-colums']}>
                {
                  csotdData
                  &&
                  <Csotd
                    {
                    ...{
                      ...csotdData,
                      imgUrl: csotdData?.filename,
                      promotion: csotdData?.promotion,
                      crazyDeal: csotdData?.crazyDeal
                    }
                    }
                  ></Csotd>
                }

                {
                  !!(topSellersData && topSellersData.length) &&
                  <CrazyTopSellers topSellers={topSellersData}></CrazyTopSellers>
                }
              </div>
            }

            {/* Featured Deals */}
            {
              !!(featuredDealsData && featuredDealsData.length) &&
              <FeaturedDeals featuredDealsData={featuredDealsData}></FeaturedDeals>
            }

            {/* Featured Deals */}
            {
              !!(hotSchemaData && hotSchemaData.length) &&
              <HotSchema hotSchemaData={hotSchemaData}></HotSchema>
            }

            {
              !!(midBanner && midBanner.imgUrl && midBannerPad && midBannerPad.imgUrl) &&
              <MiddleSubscribe midBanner={midBanner} midBannerPad={midBannerPad} />
            }
          </div>

          <div className={styles['about-box']}>
            <div className={styles['header']}>
              About Hottest Deals
            </div>

            <div className={styles['content']}>
              At Best Deals we keep an eye on what you like so that we can provide more choices, more styles, more colours and ultimately make your shopping experience more awesome!<br /><br />
              Here under hottest deals you&apos;ll find all of our most popular products, so you can check out what everyone else is buying and loving! Maybe you?ll love it too. You never know which products will turn up in our hottest deals page so keep an eye on this space.<br /><br />
              The perfect gift for your friend might show up just in time for their birthday! Something you&apos;ve been saving for might be reduced and save you even more money! We bring the deals direct to your fingertips! Get ready to be pleasantly surprised on your next purchase.<br /><br />
              Best Deals is committed to bringing you awesome online shopping experiences because we value your happiness. To that end we work hard to find extra savings for you on your Best Deals purchases. We hope you keep enjoying your shopping experience with us and remember to check our hottest deals page because the deals are amazing!
            </div>
          </div>

        </div>
      </DefaultLayout>
    </>
  )
}
